import React, {useMemo, useState} from 'react';
import {Checkbox, Stack, Typography} from '@mui/material';
import {
    MaterialReactTable,
    MRT_BottomToolbar,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner
} from 'material-react-table';
import {MRT_Localization_RU} from "material-react-table/locales/ru";
import {Link} from 'react-router-dom';
import Box from "@mui/material/Box";
import DateRangeFilter from "../../../../filter/DateRangeFilter";
import DateTimeUtil from "../../../../../../utils/common/DateTimeUtil";
import {estimateStatuses} from "../../../../../../data/EstimateState";
import ButtonCreate from "../../../../button/ButtonCreate";
import DateInput from "../../../../filter/DateInput";
import FilterUtil from "../../../../../../utils/common/FilterUtil";
import OrderFolderNameCell from "../order/OrderFolderNameCell";
import OrderUtil from "../../../../../../utils/orders/OrderUtil";
import {EMPTY_MODEL_FILTER} from "../../../../../../data/OrderStatus";

const hasHandPrice = (row) =>
    row?.estimate?.basePrice?.handPrice === true ||
    row?.estimate?.baseJobPrice?.handPrice === true;

const getEstimatePrice = (row, field) => {
    const price = row?.estimate?.[field]?.price;
    return price == null ? null : Number(price);
};

const sortByEstimatePrice = (field) => (rowA, rowB) => {
    const a = getEstimatePrice(rowA.original, field);
    const b = getEstimatePrice(rowB.original, field);
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a === b ? 0 : a > b ? 1 : -1;
};

const OrderEconomicTable = ({
                                data,
                                setData,
                                tableOptions,
                                tableOptionsHandle,
                                handleSubmitExport,
                                handleModalAlert,
                                getData,
                                startInitValue,
                                customerList,
                                nameList,
                                modelList,
                                planningList,
                                numberList,
                                numberCustomerList,
                                planning,
                                notConstructed,
                                globalFilter,
                                handleSearchQuery,
                                filteredData,
                                columnsFilter,
                            }) => {
    const [date, setDate] = useState({
        startShipped: startInitValue,
        finishShipped: null,
        startReceipt: null,
        endReceipt: null,
        startManufactured: null,
        endManufactured: null
    });
    const [showColumnFilters, setShowColumnFilters] = useState(false)

    const handleShowColumnFilters = () => {
        setShowColumnFilters(!showColumnFilters)
        if (showColumnFilters) {
            handleSearchQuery([], data, null, true)
        }
    }
    const applyFilter = (newDate = date) => {
        const values = Object.values(newDate).map(el => DateTimeUtil.convertToJavaDate(el))
        getData(globalFilter, tableOptions.pageSize, ...values)
    }

    const cancelFilter = (clear) => {
        const newDate = {
            startShipped: clear ? null : startInitValue,
            finishShipped: null,
            startReceipt: null,
            endReceipt: null,
            startManufactured: null,
            endManufactured: null
        }
        setDate(newDate)
        applyFilter(newDate)
    }

    const filterExport = (newDate = date) => {
        const values = Object.values(newDate).map(el => DateTimeUtil.convertToJavaDate(el))
        handleSubmitExport(...values)
    }

    const calcSum = (type, rows, margin = false) => {
        let marginNum = 0
        const newData = rows.reduce((acc, row) => {
            if (row.original.estimate[type] !== null && row.original.estimate[type] !== undefined) {
                if (margin) {
                    marginNum += 1
                    return acc + row.original.estimate[type];
                } else {
                    return acc + row.original.estimate[type].price;
                }
            } else {
                return acc;
            }
        }, 0)

        return margin ? (newData / marginNum)?.toLocaleString?.('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }) : newData?.toLocaleString?.('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })

    }

    const columns = useMemo(() => {
        const baseColumns = [
            {
                id: 'number',
                header: '№ заказа',
                accessorKey: 'number',
                filterVariant: 'multi-select',
                filterSelectOptions: numberList,
                minSize: 250,
                muiFilterTextFieldProps: {
                    placeholder: 'Отфильтровать по № заказа',
                    sx: {width: '200px'},
                },
                Cell: ({cell, row, renderedCellValue}) => (
                    <OrderFolderNameCell order={row.original}>
                        <Link
                            to={'/organization/orders-cloud/' + row.original.id + '/' + encodeURIComponent(row.original.numberCustomer)}
                        >
                            {cell.getValue() === null ? 'Не занумерован' : renderedCellValue}
                        </Link>
                    </OrderFolderNameCell>
                ),
            },
            {
                id: 'hand-price',
                header: 'Ручная цена',
                accessorKey: 'estimate',
                size: 8,
                enableEditing: false,
                filterVariant: 'multi-select',
                filterSelectOptions: FilterUtil.statusList,
                filterFn: (row, id, filterValue) => {
                    return FilterUtil.checkStatus(hasHandPrice(row.original), filterValue)
                },
                Cell: ({ cell, row }) => (
                    <Checkbox
                        checked={hasHandPrice(row.original)}
                        disabled={true}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 22 } }}
                    />
                )
            },
            {
                id: 'customer',
                header: 'Заказчик',
                accessorKey: 'customerName',
                enableStickyHeader: true,
                enableStickyFooter: true,
                enableGrouping: true,
                filterVariant: 'multi-select',
                filterSelectOptions: customerList,
                minSize: 400,
            },
            {
                id: 'numberCustomer',
                header: '№ заказчика',
                accessorKey: 'numberCustomer',
                filterVariant: 'multi-select',
                filterSelectOptions: numberCustomerList,
                maxSize: 23,
                minSize: 35,
                muiFilterTextFieldProps: {
                    placeholder: 'Отфильтровать по № заказчика',
                    sx: {width: '200px'},
                },
            },
            {
                id: 'header',
                header: 'Наименование',
                accessorFn: (row) => row.header?.name,
                filterVariant: 'multi-select',
                filterSelectOptions: nameList,
                enableGrouping: true,
                maxSize: 5,
            },
            {
                id: 'models',
                header: 'Модели',
                accessorFn: (row) => OrderUtil.getEstimateModuleNames(row).join(', '),
                filterVariant: 'multi-select',
                filterSelectOptions: modelList,
                enableGrouping: true,
                enableEditing: false,
                size: Math.max(
                    80,
                    ...['Модели', ...(modelList || []).filter((name) => name !== EMPTY_MODEL_FILTER)]
                        .map((name) => String(name).length * 9)
                ),
                Cell: ({row}) => {
                    const names = OrderUtil.getEstimateModuleNames(row.original);
                    if (!names.length) {
                        return null;
                    }
                    return (
                        <Box sx={{display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap'}}>
                            {names.map((name, index) => (
                                <Box key={`${name}-${index}`}>{name}</Box>
                            ))}
                        </Box>
                    );
                },
                muiFilterTextFieldProps: {
                    placeholder: 'Отфильтровать по модели',
                    sx: {
                        minWidth: '200px',
                        width: 'auto',
                    },
                }
            },
            {
                id: 'plan',
                header: 'План',
                accessorFn: (row) => row.plan?.name,
                filterVariant: 'multi-select',
                filterSelectOptions: planningList,
                minSize: 6,
                maxSize: 6,
                Cell: ({row, renderedCellValue}) => (
                    <>
                        {row.original?.plan !== null && (
                            <Link
                                to={'/organization/plans/' + row.original?.plan?.id + "/" + encodeURIComponent(row.original?.plan?.name)}>{renderedCellValue}</Link>
                        )}
                    </>
                ),
            },
            {
                id: 'calculationTotalCostBasePrice',
                header: `СБ план:`,
                accessorKey: 'estimate.calculationTotalCostBasePrice',
                minSize: 15,
                maxSize: 15,
                sortingFn: sortByEstimatePrice('calculationTotalCostBasePrice'),
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                Footer: ({column, footer, table}) => {
                    return <Stack>
                        <Box color="warning.main">
                            {calcSum("calculationTotalCostBasePrice", table.getRowModel().rows)}
                        </Box>
                    </Stack>
                }
            },
            {
                id: 'totalCostBasePrice',
                header: `СБ факт:`,
                accessorKey: 'estimate.totalCostBasePrice',
                minSize: 15,
                maxSize: 15,
                sortingFn: sortByEstimatePrice('totalCostBasePrice'),
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                Footer: ({column, footer, table}) => {
                    return <Stack>
                        <Box color="warning.main">
                            {calcSum("totalCostBasePrice", table.getRowModel().rows)}
                        </Box>
                    </Stack>
                }
            },
            {
                id: 'baseTotalPrice',
                header: `Продажа`,
                accessorKey: 'estimate.baseTotalPrice',
                minSize: 15,
                maxSize: 15,
                sortingFn: sortByEstimatePrice('baseTotalPrice'),
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                Footer: ({column, footer, table}) => {
                    return <Stack>
                        <Box color="warning.main">
                            {calcSum("baseTotalPrice", table.getRowModel().rows)}
                        </Box>
                    </Stack>
                }
            },
            {
                id: 'profitCalculationBaseTotalPrice',
                header: `Прибыль план:`,
                accessorKey: 'estimate.profitCalculationBaseTotalPrice',
                minSize: 15,
                maxSize: 15,
                sortingFn: sortByEstimatePrice('profitCalculationBaseTotalPrice'),
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                Footer: ({column, footer, table}) => {
                    return <Stack>
                        <Box color="warning.main">
                            {calcSum("profitCalculationBaseTotalPrice", table.getRowModel().rows)}
                        </Box>
                    </Stack>
                }
            },
            {
                id: 'profitBaseTotalPrice',
                header: `Прибыль факт:`,
                accessorKey: 'estimate.profitBaseTotalPrice',
                minSize: 15,
                maxSize: 15,
                sortingFn: sortByEstimatePrice('profitBaseTotalPrice'),
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                Footer: ({column, footer, table}) => {
                    return <Stack>
                        <Box color="warning.main">
                            {calcSum("profitBaseTotalPrice", table.getRowModel().rows)}
                        </Box>
                    </Stack>
                }
            },
            {
                id: 'calculationGrossMargin',
                header: `Валовая маржа план`,
                accessorKey: 'estimate.calculationGrossMargin',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.toFixed(2)
                },
                Footer: ({column, footer, table}) => {
                    return <Stack>
                        <Box color="warning.main">
                            {calcSum("calculationGrossMargin", table.getRowModel().rows, true)}
                        </Box>
                    </Stack>
                }
            },
            {
                id: 'grossMargin',
                header: `Валовая маржа факт`,
                accessorKey: 'estimate.grossMargin',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.toFixed(2)
                },
                Footer: ({column, footer, table}) => {
                    return <Stack>
                        <Box color="warning.main">
                            {calcSum("grossMargin", table.getRowModel().rows, true)}
                        </Box>
                    </Stack>
                }
            },
            {
                id: 'estimateState',
                header: 'Статус',
                accessorKey: 'estimate.estimateState',
                minSize: 20,
                maxSize: 20,
                Cell: ({cell}) => estimateStatuses[cell.getValue()],
                filterFn: (row, id, filterValue) => {
                    return estimateStatuses[row.original.estimate?.estimateState].toLowerCase().includes(filterValue.toLowerCase())
                },
            },
            {
                accessorFn: (row) => {
                    if (row.dateOfReceipt === null) {
                    } else {
                        return new Date(row.dateOfReceipt)
                    }
                },
                id: 'dateOfReceipt',
                header: 'Дата поступления',
                sortingFn: 'datetime',
                maxSize: 25,
                Cell: ({cell}) => {
                    if (cell.getValue() !== undefined) {
                        return cell.getValue().toLocaleDateString();
                    }
                },
                filterFn: (row, id, filterValue) => {
                    const startDate = filterValue?.startDate;
                    const endDate = filterValue?.endDate;
                    if (startDate && endDate) {
                        return (
                            DateTimeUtil.checkDateBefore(row.getValue(id), startDate) &&
                            DateTimeUtil.checkDateAfter(row.getValue(id), endDate)
                        );
                    }
                    return true;
                },
                Filter: ({column}) => <DateRangeFilter
                    column={column}
                />,
            },
            {
                accessorFn: (row) => {
                    if (row.manufactured === null) {
                    } else {
                        return new Date(row.manufactured)
                    }
                },
                id: 'manufactured',
                header: 'Дата производства',
                sortingFn: 'datetime',
                maxSize: 25,
                Cell: ({cell}) => {
                    if (cell.getValue() !== undefined) {
                        return cell.getValue().toLocaleDateString();
                    }
                },
                filterFn: (row, id, filterValue) => {
                    const startDate = filterValue?.startDate;
                    const endDate = filterValue?.endDate;
                    if (startDate && endDate) {
                        return (
                            DateTimeUtil.checkDateBefore(row.getValue(id), startDate) &&
                            DateTimeUtil.checkDateAfter(row.getValue(id), endDate)
                        );
                    }
                    return true;
                },
                Filter: ({column}) => <DateRangeFilter
                    column={column}
                />,
            },
            {
                accessorFn: (row) => {
                    if (row.shipped === null) {
                    } else {
                        return new Date(row.shipped)
                    }
                },
                id: 'shipped',
                header: 'Дата отгрузки',
                sortingFn: 'datetime',
                maxSize: 25,
                Cell: ({cell}) => {
                    if (cell.getValue() !== undefined) {
                        return cell.getValue().toLocaleDateString();
                    }
                },
                filterFn: (row, id, filterValue) => {
                    const startDate = filterValue?.startDate;
                    const endDate = filterValue?.endDate;
                    if (startDate && endDate) {
                        return (
                            DateTimeUtil.checkDateBefore(row.getValue(id), startDate) &&
                            DateTimeUtil.checkDateAfter(row.getValue(id), endDate)
                        );
                    }
                    return true;
                },
                Filter: ({column}) => <DateRangeFilter
                    column={column}
                />,
            },
            {
                id: 'year',
                header: 'год                                                                     ',
                accessorKey: 'year',
                enableColumnActions: false,
                enableGrouping: false,
                enableColumnFilter: false,
                enableEditing: false,
            },
            {
                header: 'id',
                accessorKey: 'id',
                enableHiding: false,
                enableColumnActions: false,
                enableGrouping: false,
                enableColumnFilter: false,
                enableEditing: false,
            },
        ];

        return baseColumns;

    }, [data, setData, filteredData, customerList, nameList, modelList, planningList, numberList, numberCustomerList]);

    return (
        <>
            <Box sx={{
                display: 'flex',
                justifyContent: "space-between",
                flexDirection: "column",
                gap: "16px",
                paddingBottom: (planning || notConstructed) ? "20px" : 0
            }}>
                <Box
                    sx={{display: 'flex', justifyContent: "space-between", gap: "16px"}}>
                    <Typography
                        sx={{fontSize: "24px", fontWeight: 700}}>
                        {notConstructed ? "Не разработано" : planning ? "Планирование" : "Экономика"}
                    </Typography>
                </Box>
                {!(planning || notConstructed) && <Box
                    sx={{display: 'flex', justifyContent: "flex-end"}}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: "spase-between",
                            alignItems: "flex-end",
                            paddingBottom: "30px",
                            gap: "9px"
                        }}>
                        <Box>
                            <Typography sx={{
                                fontSize: "16px",
                                fontWeight: 700
                            }}>{"Отгружено от"}</Typography>
                            <DateInput
                                value={date.startShipped}
                                setValue={(value) => setDate(prev => ({...prev, startShipped: value}))}
                                variant={"outlined"}
                            />
                        </Box>
                        <Box>
                            <Typography sx={{fontSize: "16px", fontWeight: 700}}>Отгружено до</Typography>
                            <DateInput
                                value={date.finishShipped}
                                setValue={(value) => setDate(prev => ({...prev, finishShipped: value}))}
                                helperText={"Отгружено до"}
                                variant={"outlined"}
                            />
                        </Box>
                        <Box>
                            <Typography sx={{fontSize: "16px", fontWeight: 700}}>Получено от</Typography>
                            <DateInput
                                variant={"outlined"}
                                value={date.startReceipt}
                                setValue={(value) => setDate(prev => ({...prev, startReceipt: value}))}
                            />
                        </Box>
                        <Box>
                            <Typography sx={{fontSize: "16px", fontWeight: 700}}>Получено до</Typography>
                            <DateInput
                                value={date.endReceipt}
                                setValue={(value) => setDate(prev => ({...prev, endReceipt: value}))}
                                variant={"outlined"}
                            />
                        </Box>
                        <Box>
                            <Typography sx={{fontSize: "16px", fontWeight: 700}}>Произведено от</Typography>
                            <DateInput
                                value={date.startManufactured}
                                setValue={(value) => setDate(prev => ({...prev, startManufactured: value}))}
                                variant={"outlined"}
                            />
                        </Box>
                        <Box>
                            <Typography sx={{fontSize: "16px", fontWeight: 700}}>Произведено до</Typography>
                            <DateInput
                                value={date.endManufactured}
                                setValue={(value) => setDate(prev => ({...prev, endManufactured: value}))}
                                variant={"outlined"}
                            />
                        </Box>
                        <ButtonCreate
                            text={"Применить фильтр"}
                            width={170}
                            height={43}
                            icon={false}
                            onClick={() => applyFilter()}
                        />
                        <ButtonCreate
                            text={"Отменить фильтр"}
                            width={160}
                            height={43}
                            icon={false}
                            onClick={() => cancelFilter(false)}
                        />
                        <ButtonCreate
                            text={"Очистить фильтр"}
                            width={160}
                            height={43}
                            icon={false}
                            onClick={() => cancelFilter(true)}
                        />
                        <ButtonCreate
                            text={"Экспорт"}
                            width={80}
                            height={43}
                            icon={false}
                            onClick={() => filterExport()}
                        />
                    </Box>
                </Box>}
            </Box>

            <MaterialReactTable
                renderTopToolbar={({table}) => {
                    return <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "7px",
                        backgroundColor: (theme) => table.getSelectedRowModel().rows.length > 0 ? theme.palette.primary.light : "transparent",
                    }}>
                        <MRT_ToolbarAlertBanner table={table}/>
                        <Box sx={{display: "flex"}}>
                            <MRT_ToggleFiltersButton table={table}/>
                            <MRT_GlobalFilterTextField table={table}/>
                        </Box>
                    </Box>
                }}
                selectAllMode={"all"}
                enableColumnFilterModes
                enableGrouping
                enableStickyHeader
                enablePinning
                muiTableBodyCellProps={{
                    sx: {
                        p: '0px 0px 0px 10px',
                        m: '0px 0px 0px 0px',
                        border: "none",
                        "&:after": {
                            display: "none",
                        },
                        "& td:after": {
                            "&:hover": {
                                backgroundColor: "red"
                            }
                        },
                        "&:not(:last-of-type)": {
                            borderRight: '1px solid #EAEBEA',

                        },

                        "& a": {
                            color: "#006CCC",
                            textDecoration: "none",
                            "&:hover": {
                                textDecoration: "underline"
                            }
                        }

                    },
                }}
                muiTableHeadCellProps={{
                    sx: {
                        border: '1px solid #EAEBEA',
                        backgroundColor: "#F5F6FA",
                        padding: "10px",
                        paddingBottom: " 0.2rem",
                        paddingTop: "0.2rem"
                    },
                }}
                muiTableBodyRowProps={({row}) => {
                    return ({
                        sx: () => ({
                            height: '36px',
                            backgroundColor: "white",
                            "&:hover& td:after": {
                                display: "none",
                            },
                            "&.MuiTableRow-root.Mui-selected": {
                                backgroundColor: (theme) => theme.palette.primary.light70,
                                "&:hover": {
                                    backgroundColor: (theme) => theme.palette.primary.light50,
                                },
                            },
                            "&:hover": {
                                "& td:after": {
                                    display: "none",
                                },
                                backgroundColor: (theme) => theme.palette.primary.stroke,
                            },
                            '&:nth-of-type(even)': {
                                "&.MuiTableRow-root.Mui-selected": {
                                    backgroundColor: (theme) => theme.palette.primary.light,
                                    "&:hover": {
                                        backgroundColor: (theme) => theme.palette.primary.light50,
                                    },
                                },
                                backgroundColor: (theme) => theme.palette.primary.lightGrey,
                                "&:hover": {
                                    backgroundColor: (theme) => theme.palette.primary.stroke,
                                },
                            },
                        })
                    })
                }}

                displayColumnDefOptions={{
                    'mrt-row-actions': {
                        header: "",
                        size: 3,
                        minSize: 3,
                        grow: false,
                    },
                }}
                muiSearchTextFieldProps={{
                    placeholder: `Поиск`,
                    sx: {
                        paddingTop: "5px",
                        paddingBottom: "0px",
                        "& .MuiCollapse-root": {
                            borderRadius: '12px',
                            height: 40, width: 290,
                            "& .MuiSvgIcon-root": {
                                color: "#737478"
                            },
                            '& .MuiOutlinedInput-input': {
                                padding: 0
                            }
                        }
                    },
                    variant: 'outlined',
                }}
                columns={columns}
                data={filteredData}
                state={{
                    showColumnFilters: showColumnFilters,
                    columnFilters: columnsFilter,
                    globalFilter: globalFilter,
                    showGlobalFilter: true,
                    pagination: tableOptions,
                }}
                onPaginationChange={(updater) => {
                    tableOptionsHandle(updater(tableOptions));
                }}
                onGlobalFilterChange={(value) => {
                    handleSearchQuery(value, data, null)
                }}
                onColumnFiltersChange={(updater) => {
                    handleSearchQuery(updater(columnsFilter), data, null, true)
                }}
                onShowColumnFiltersChange={handleShowColumnFilters}
                manualFiltering={true}
                rowCount={tableOptions.totalElements}
                getRowId={(originalRow) => originalRow.id}
                selectionPadding={1}
                selectionCss={{
                    backgroundColor: 'lightblue',
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: 'blue',
                    borderRadius: '4px',
                }}
                initialState={{
                    columnVisibility: {
                        id: false,
                        model: false,
                        year: false,
                    },
                }}
                muiToolbarAlertBannerChipProps={{color: 'primary'}}
                localization={MRT_Localization_RU}
                enableRowActions={false}
                enableToolbarInternalActions={false}
                renderBottomToolbar={({table}) => {
                    const ordersCount = filteredData?.length ?? 0
                    const calculatedCount = (filteredData || []).filter(hasHandPrice).length
                    const isGrouped = table.getState().grouping.length > 0
                    const pageSizeLabel = isGrouped
                        ? 'Сгруппированных элементов на странице:'
                        : 'Элементов на странице:'
                    const rowsPerPage = `Всего заказов: ${ordersCount}. Посчитанных заказов: ${calculatedCount}. ${pageSizeLabel}`
                    const tableProps = {
                        ...table,
                        options: {
                            ...table.options,
                            localization: {
                                ...table.options.localization,
                                rowsPerPage,
                            }
                        }
                    }
                    return <MRT_BottomToolbar table={tableProps}/>
                }}
            />
        </>
    );
};

export default OrderEconomicTable;